import type { ProgressSummary } from "@/lib/api/api-client";
import {
  journeyStorageKey,
  PROGRESS_SCHEMA_VERSION,
  type JourneyProgress,
  type StorageLike,
} from "@/lib/learning-journey/progress-store";

/**
 * Où en est-on d'un morceau, vu depuis « Mes morceaux ».
 *
 * # Pourquoi ça ne coûte rien au serveur
 *
 * `LibraryEntryItem.midiFileId` **est** le `morceauId` sous lequel le parcours
 * range sa progression (`virtuosa:journey:<id>`). La ligne de « Mes morceaux »
 * peut donc dire où l'on en est sans une seule ligne de back-end — la
 * progression est déjà dans le navigateur qui affiche la liste.
 *
 * # Trois états, et pas un de plus
 *
 *  - **En cours** — « Phrase 7 sur 19 » ;
 *  - **Tu le joues** — le morceau est passé au répertoire ;
 *  - **À revoir** — il y est, mais la dernière traversée n'a pas tenu.
 *
 * ## Ce qu'on n'affiche pas, et pourquoi
 *
 * **Aucun pourcentage de réussite.** Un « 78 % » sur une carte est un bulletin
 * scolaire : il note la personne au lieu de décrire le morceau, et il n'a même
 * pas le mérite d'être actionnable — on ne sait pas quoi faire d'un chiffre
 * qui baisse.
 *
 * **Aucun calendrier de répétition espacée.** `Repertoire.due()` renvoie *une*
 * offre ou rien, jamais une file, et `lib/learning-journey/repertoire.ts`
 * explique longuement pourquoi : un plafond qui n'existe que dans le rendu est
 * un plafond qu'on demandera de lever, et alors c'est une liste, et le nombre
 * qu'elle affiche est un retard à rattraper. « À revoir » ici ne porte aucune
 * date, aucune échéance et aucun rang : c'est un constat sur la dernière
 * traversée, pas une convocation.
 *
 * # Le hachage de plan n'est pas vérifié
 *
 * `ProgressStore.load` invalide la sauvegarde quand `phrasePlanHash` a changé
 * — c'est juste pour *rejouer* le parcours, dont l'état interne serait
 * incohérent. Ici, on ne rejoue rien : on répond « as-tu travaillé ce morceau,
 * et jusqu'où ». Une transposition change le hachage sans effacer ce fait, et
 * faire disparaître la mention parce que la personne a transposé serait faux.
 *
 * Corollaire assumé : un `morceauId` suffixé par une transposition est une
 * autre clé, donc une session transposée n'a pas de mention sous la clé de
 * base. Rien plutôt qu'un état emprunté à une autre tonalité.
 *
 * Module pur : ni React, ni DOM (le stockage entre par `StorageLike`). Testé
 * dans `__tests__/journey-glance.test.ts`.
 */

export type JourneyGlance =
  | {
      readonly kind: "in-progress";
      /** Rang de la phrase en cours, à partir de 1. */
      readonly phraseIndex: number;
      readonly phraseTotal: number;
    }
  | { readonly kind: "played" }
  | { readonly kind: "revisit" };

/**
 * Lit la progression rangée pour `morceauId`, `null` quand il n'y en a pas, ou
 * qu'elle n'est pas lisible par ce bundle.
 *
 * Ne lève jamais et n'écrit jamais : un stockage refusé (mode privé, quota) ou
 * une charge écrite par un schéma plus récent se lit comme « rien à dire »,
 * pas comme une erreur à remonter à l'écran.
 */
export function readJourneyGlance(
  storage: StorageLike,
  morceauId: string,
): JourneyGlance | null {
  const progress = readJourneyProgress(storage, morceauId);

  return progress === null ? null : journeyGlanceFrom(progress);
}

/**
 * La même déduction, sur un document déjà en main.
 *
 * Extraite de `readJourneyGlance` parce qu'elle a désormais deux appelants qui
 * n'ont pas le même stockage : la carte lit `localStorage`, et
 * `SyncingProgressStore` tient le document qu'il s'apprête à pousser. Refaire
 * la déduction des deux côtés serait deux occasions de diverger — c'est déjà
 * l'argument de `readJourneyProgress` juste en dessous.
 *
 * `null` veut dire « rien à dire » : un parcours dont le plan de phrases est
 * vide n'a pas encore commencé.
 */
export function journeyGlanceFrom(
  progress: JourneyProgress,
): JourneyGlance | null {
  if (progress.phaseGlobale === "repertoire") {
    const held = progress.lastTraversal?.held ?? true;

    return held ? { kind: "played" } : { kind: "revisit" };
  }

  const phraseTotal = progress.phrases.length;

  if (phraseTotal === 0) {
    return null;
  }

  const done = progress.phrases.filter(
    (phrase) => phrase.etatMax === "DONE",
  ).length;

  return {
    kind: "in-progress",
    // La phrase *en cours* est celle qui suit la dernière validée. Bornée au
    // total : toutes validées sans passage au répertoire arrive pendant la
    // frame où la phase n'a pas encore basculé.
    phraseIndex: Math.min(done + 1, phraseTotal),
    phraseTotal,
  };
}

/**
 * Le même état, dans la forme que le serveur range.
 *
 * C'est le seul endroit qui connaisse la correspondance entre les trois `kind`
 * d'ici et les trois `state` de l'API. Elle vit là plutôt que dans le client
 * HTTP parce que c'est ce module qui possède le vocabulaire des trois états :
 * le client, lui, ne fait que transporter.
 *
 * Les compteurs de phrase ne partent que sur `in_progress`. Ailleurs ils
 * n'auraient rien à compter, et le serveur les refuserait à `0`.
 */
export function journeySummaryFrom(
  progress: JourneyProgress,
): ProgressSummary | null {
  const glance = journeyGlanceFrom(progress);

  if (glance === null) {
    return null;
  }

  if (glance.kind === "in-progress") {
    return {
      state: "in_progress",
      phraseIndex: glance.phraseIndex,
      phraseTotal: glance.phraseTotal,
    };
  }

  return { state: glance.kind, phraseIndex: null, phraseTotal: null };
}

/**
 * Le chemin inverse : le résumé que le **serveur** sert, ramené au vocabulaire
 * d'ici.
 *
 * C'est ce que lisent désormais « Mes morceaux » et « Là où tu t'es arrêté ».
 * Avant, la carte lisait `localStorage` : la mention n'existait alors que sur
 * l'appareil où le morceau avait été travaillé, et changer de téléphone
 * effaçait tout ce qu'on avait fait. `LibraryEntryItem.practice` arrive avec la
 * ligne, donc elle est juste partout.
 *
 * `journeySummaryFrom` juste au-dessus fait le trajet aller. Les deux vivent
 * ici, côte à côte, parce que la correspondance entre les trois `kind` et les
 * trois `state` n'a qu'un propriétaire.
 *
 * `null` quand le résumé est incohérent : `in_progress` sans compteurs de
 * phrase ne se rend pas en « Phrase … sur … ». Le serveur les exige, mais un
 * document écrit par une version antérieure du client peut encore en manquer,
 * et rien vaut mieux qu'un « Phrase null sur null ».
 */
export function journeyGlanceFromSummary(
  summary: ProgressSummary,
): JourneyGlance | null {
  if (summary.state === "in_progress") {
    const { phraseIndex, phraseTotal } = summary;

    if (phraseIndex === null || phraseTotal === null || phraseTotal <= 0) {
      return null;
    }

    return { kind: "in-progress", phraseIndex, phraseTotal };
  }

  return { kind: summary.state };
}

/** Le libellé affiché sur la carte. */
export function journeyGlanceLabel(glance: JourneyGlance): string {
  switch (glance.kind) {
    case "in-progress":
      return `Phrase ${glance.phraseIndex} sur ${glance.phraseTotal}`;

    case "played":
      return "Tu le joues";

    case "revisit":
      return "À revoir";
  }
}

/**
 * La progression rangée pour ce morceau, ou `null`.
 *
 * Exportée parce que l'état d'un parcours se déduit des mêmes documents que
 * la mention de « Mes morceaux ». Deux lecteurs pour un seul format seraient
 * deux occasions de diverger.
 */
export function readJourneyProgress(
  storage: StorageLike,
  morceauId: string,
): JourneyProgress | null {
  let raw: string | null;

  try {
    raw = storage.getItem(journeyStorageKey(morceauId));
  } catch {
    return null;
  }

  if (raw === null) {
    return null;
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!isJourneyProgress(parsed)) {
    return null;
  }

  return parsed;
}

/**
 * La même lecture, sur un document qui n'est pas venu du stockage local.
 *
 * Le rattrapage des résumés (`hydrate-progress.ts`) résume des documents rendus
 * par l'API, où ils arrivent en `Record<string, unknown>`. Sans ce point
 * d'entrée, il lui faudrait sa propre garde de forme — et le schéma aurait un
 * second propriétaire, ce que tout ce module s'emploie à éviter.
 */
export function toJourneyProgress(value: unknown): JourneyProgress | null {
  return isJourneyProgress(value) ? value : null;
}

/**
 * Garde de forme minimale : la version de schéma, la phase et le tableau de
 * phrases, c'est-à-dire exactement ce que ce module lit. Valider davantage
 * ferait de ce lecteur un second propriétaire du schéma.
 */
function isJourneyProgress(value: unknown): value is JourneyProgress {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Partial<JourneyProgress>;

  return (
    candidate.schemaVersion === PROGRESS_SCHEMA_VERSION &&
    typeof candidate.phaseGlobale === "string" &&
    Array.isArray(candidate.phrases)
  );
}
